(() => {
  if (customElements.get('emz-made-with')) return;
  const isHandle = value => /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(value || '');
  const isId = value => /^[1-9]\d*$/.test(value || '');
  class MadeWith extends HTMLElement {
    connectedCallback() {
      this.cancel();
      this.list = this.querySelector('[data-made-list]');
      this.status = this.querySelector('[data-made-status]');
      this.retry = this.querySelector('[data-made-retry]');
      this.curatedNote = this.querySelector('[data-made-curated]');
      this.more = this.querySelector('[data-made-more]');
      if (!this.list || !this.status || !this.retry || !isHandle(this.dataset.handle)) return;
      const context = [this.dataset.theme, this.dataset.locale, this.dataset.currency, this.dataset.handle].join('|');
      this.expanded = this.rememberedExpansion(context);
      this.expansionContext = context;
      this.resetMore();
      this.classList.remove('is-empty');
      if (this.curatedNote) this.curatedNote.textContent = '';
      this.list.replaceChildren();
      this.list.hidden = true;
      this.removeAttribute('aria-busy');
      this.status.textContent = '';
      this.retry.hidden = true;
      this.onRetry = () => this.start();
      this.retry.addEventListener('click', this.onRetry);
      this.onMore = () => this.expand();
      this.more?.addEventListener('click', this.onMore);
      this.onProduct = event => this.rememberProduct(event);
      window.addEventListener('click', this.onProduct);
      this.onShow = event => {
        this.documentShown = true;
        if (event.persisted) { this.cancelReturn(); this.consumeReturn(); }
        else if (this.returnTask) { this.returnTask.shown = true; this.restoreReturn(); }
      };
      this.onHide = () => { this.finishProductRecord(); this.cancelReturn(); this.documentShown = false; };
      window.addEventListener('pageshow', this.onShow);
      window.addEventListener('pagehide', this.onHide);
      if (this.dataset.ready !== 'true') return;
      this.prepareReturn();
      if ('IntersectionObserver' in window && !this.expanded && !this.returnTask) {
        this.observer = new IntersectionObserver(entries => {
          if (entries.some(entry => entry.isIntersecting)) {
            this.observer.disconnect();
            this.start();
          }
        }, { rootMargin: '600px' });
        this.observer.observe(this);
      } else this.start();
    }
    disconnectedCallback() {
      this.cancel();
      this.cancelReturn();
      this.retry?.removeEventListener('click', this.onRetry);
      this.more?.removeEventListener('click', this.onMore);
      window.removeEventListener('click', this.onProduct);
      window.removeEventListener('pageshow', this.onShow);
      window.removeEventListener('pagehide', this.onHide);
      clearTimeout(this.rememberTimer);
      this.pendingProductRecord = null;
    }
    rememberProduct(event) {
      clearTimeout(this.rememberTimer);
      this.pendingProductRecord = null;
      if (window.Shopify?.designMode || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const link = event.target.closest?.('a.emz-made-with__product[href]');
      const card = link?.closest('.emz-made-with__card');
      const target = link?.target || document.querySelector('base[target]')?.target;
      if (!card || card.parentElement !== this.list || card.hidden || !isHandle(card.dataset.madeHandle) || link.hasAttribute('download') || (target && target !== '_self') || link.origin !== location.origin || !link.pathname.endsWith('/products/' + card.dataset.madeHandle)) return;
      const url = location.href;
      this.pendingProductRecord = () => {
        if (event.defaultPrevented || !this.isConnected || !link.isConnected || location.href !== url) return;
        try {
          const state = history.state;
          if (state !== null && Object.prototype.toString.call(state) !== '[object Object]') return;
          const saved = state?.emzMadeWith;
          const previous = saved?.version === 1 && saved.context === this.expansionContext ? saved : {};
          history.replaceState({ ...state, emzMadeWith: { ...previous, version: 1, context: this.expansionContext, expanded: this.expanded === true, returnTo: { handle: card.dataset.madeHandle, url } } }, '');
        } catch (_) {}
      };
      this.rememberTimer = setTimeout(() => this.finishProductRecord(), 0);
    }
    finishProductRecord() {
      clearTimeout(this.rememberTimer);
      const record = this.pendingProductRecord;
      this.pendingProductRecord = null;
      record?.();
    }
    consumeReturn() {
      try {
        const state = history.state;
        const saved = state?.emzMadeWith;
        if (Object.prototype.toString.call(state) !== '[object Object]' || saved?.version !== 1 || saved.context !== this.expansionContext || !saved.returnTo) return null;
        const { returnTo, ...remaining } = saved;
        history.replaceState({ ...state, emzMadeWith: remaining }, '');
        return returnTo;
      } catch (_) { return null; }
    }
    prepareReturn() {
      if (this.returnPrepared || window.Shopify?.designMode) return;
      this.returnPrepared = true;
      try {
        const returnTo = this.consumeReturn();
        if (!returnTo || performance.getEntriesByType?.('navigation')[0]?.type !== 'back_forward' || returnTo.url !== location.href || !isHandle(returnTo.handle)) return;
        const task = { handle: returnTo.handle, url: location.href, controller: new AbortController(), shown: this.documentShown === true };
        this.returnTask = task;
        task.deadline = setTimeout(() => this.cancelReturn(), 20000);
        const options = { signal: task.controller.signal, capture: true, passive: true };
        for (const name of ['pointerdown', 'touchstart', 'wheel', 'keydown', 'hashchange']) window.addEventListener(name, () => this.cancelReturn(), options);
        window.addEventListener('popstate', event => {
          const entry = event.state?.emzMadeWith;
          const target = entry?.returnTo;
          if (!task.sawTraversal && location.href === task.url && entry?.version === 1 && entry.context === this.expansionContext && entry.expanded === this.expanded && (target === undefined || (target?.handle === task.handle && target.url === task.url))) task.sawTraversal = true;
          else this.cancelReturn();
        }, options);
        document.addEventListener('visibilitychange', () => { if (document.hidden) this.cancelReturn(); }, options);
      } catch (_) { this.cancelReturn(); }
    }
    cancelReturn() {
      if (!this.returnTask) return;
      this.returnTask.controller.abort();
      cancelAnimationFrame(this.returnTask.frame);
      clearTimeout(this.returnTask.deadline);
      this.returnTask = null;
    }
    restoreReturn() {
      const task = this.returnTask;
      if (!task || !task.shown || !task.run || task.frame) return;
      task.frame = requestAnimationFrame(() => {
        task.frame = requestAnimationFrame(() => {
          if (this.returnTask !== task) return;
          const card = [...this.list.children].find(card => card.dataset.madeHandle === task.handle && !card.hidden);
          const idleFocus = !document.activeElement || document.activeElement === document.body || document.activeElement === document.documentElement;
          const restore = this.owns(task.run) && location.href === task.url && !document.hidden && idleFocus && card;
          this.cancelReturn();
          if (restore) {
            const bounds = card.getBoundingClientRect();
            const headerBottom = Math.max(0, document.getElementById('SiteHeader')?.getBoundingClientRect().bottom || 0);
            if (bounds.top < headerBottom || bounds.bottom > innerHeight) card.scrollIntoView({ block: 'center', inline: 'nearest', behavior: 'instant' });
          }
        });
      });
    }
    rememberedExpansion(context) {
      try {
        const saved = history.state?.emzMadeWith;
        return saved?.version === 1 && saved.context === context && saved.expanded === true;
      } catch (_) { return false; }
    }
    rememberExpansion() {
      try {
        const state = history.state;
        if (state !== null && Object.prototype.toString.call(state) !== '[object Object]') return;
        history.replaceState({ ...state, emzMadeWith: { version: 1, context: this.expansionContext, expanded: true } }, '');
      } catch (_) {}
    }
    resetMore() {
      if (!this.more) return;
      this.more.hidden = true;
      this.more.textContent = '';
      this.more.setAttribute('aria-expanded', 'false');
    }
    expand() {
      if (!this.more || this.more.hidden || this.hasAttribute('aria-busy')) return;
      const hidden = [...this.list.children].filter(card => card.hidden);
      if (!hidden.length) return;
      hidden.forEach(card => { card.hidden = false; });
      this.expanded = true;
      this.rememberExpansion();
      this.more.setAttribute('aria-expanded', 'true');
      hidden[0].querySelector('.emz-made-with__product')?.focus({ preventScroll: true });
      this.more.hidden = true;
    }
    cancel(keepReturn = false) {
      if (!keepReturn) this.cancelReturn();
      this.observer?.disconnect();
      if (this.run) {
        this.run.controller.abort();
        clearTimeout(this.run.deadline);
      }
      this.run = null;
    }
    owns(run) {
      return this.isConnected && this.run === run && !run.controller.signal.aborted;
    }
    async page(number, run) {
      const url = new URL(this.dataset.endpoint, location.origin);
      if (url.origin !== location.origin || !url.pathname.endsWith('/collections/all')) throw new Error('Invalid catalogue route');
      url.search = '';
      url.hash = '';
      url.searchParams.set('section_id', 'emz-made-with-index');
      url.searchParams.set('sort_by', 'title-ascending');
      url.searchParams.set('page', String(number));
      let timer;
      const work = (async () => {
        const response = await fetch(url.href, { signal: run.controller.signal, credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'text/html' } });
        if (!response.ok) throw new Error('Catalogue read failed');
        const body = await response.text();
        if (!this.owns(run) || body.length > 1000000) throw new Error('Stale or oversized catalogue');
        const doc = new DOMParser().parseFromString(body, 'text/html');
        const roots = doc.querySelectorAll('[data-made-page]');
        if (roots.length !== 1) throw new Error('Missing catalogue page');
        const root = roots[0];
        const meta = root.dataset;
        const total = Number(meta.total), pages = Number(meta.pages);
        if (meta.schema !== '1' || meta.theme !== this.dataset.theme || meta.locale !== this.dataset.locale || meta.currency !== this.dataset.currency || meta.sort !== 'title-ascending' || Number(meta.page) !== number || meta.pageSize !== '100' || !Number.isInteger(total) || total < 0 || total > 1000 || !Number.isInteger(pages) || pages < 1 || pages > 10 || pages !== Math.max(1, Math.ceil(total / 100))) throw new Error('Invalid catalogue context');
        const seen = JSON.parse(root.querySelector('[data-made-seen]')?.textContent || 'null');
        const expected = Math.min(100, Math.max(0, total - (number - 1) * 100));
        if (!Array.isArray(seen) || seen.length !== expected || seen.some(id => typeof id !== 'string' || !isId(id)) || new Set(seen).size !== seen.length) throw new Error('Incomplete catalogue page');
        const template = root.querySelector('template[data-made-cards]');
        if (!template) throw new Error('Missing product data');
        const cards = [...template.content.children];
        const cardIds = new Set();
        for (const card of cards) {
          const data = card.dataset;
          const keys = (data.madeKeys || '').split('|').filter(Boolean);
          if (card.tagName !== 'LI' || !isId(data.madeId) || !seen.includes(data.madeId) || cardIds.has(data.madeId) || !isHandle(data.madeHandle) || !keys.length || keys.some(key => !isHandle(key)) || !['true', 'false'].includes(data.madeAvailable)) throw new Error('Invalid product data');
          cardIds.add(data.madeId);
          const allowedTags = new Set(['LI', 'A', 'IMG', 'DIV', 'H3', 'P', 'SPAN', 'BUTTON']);
          for (const element of [card, ...card.querySelectorAll('*')]) {
            if (!allowedTags.has(element.tagName) || [...element.attributes].some(attr => /^on/i.test(attr.name)) || element.hasAttribute('srcset') || element.hasAttribute('style')) throw new Error('Unexpected card markup');
            if (element.tagName === 'BUTTON' && element.type !== 'button') throw new Error('Unexpected form control');
            if (element.tagName === 'A') {
              const href = new URL(element.getAttribute('href'), location.origin);
              const productLink = href.pathname.endsWith('/products/' + data.madeHandle);
              const savedLink = element.hasAttribute('data-saved-view') && href.pathname.endsWith('/search') && href.search === '?view=saved';
              if (href.origin !== location.origin || (!productLink && !savedLink)) throw new Error('Unexpected card link');
            }
            if (element.tagName === 'IMG') {
              const src = new URL(element.getAttribute('src'), location.origin);
              if (src.protocol !== 'https:' || (src.hostname !== 'cdn.shopify.com' && !(src.origin === location.origin && src.pathname.startsWith('/cdn/')))) throw new Error('Unexpected image');
            }
          }
        }
        return { total, pages, seen, cards };
      })();
      try {
        return await Promise.race([work, new Promise((_, reject) => { timer = setTimeout(() => reject(new Error('Catalogue read timeout')), 8000); })]);
      } finally { clearTimeout(timer); }
    }
    async start() {
      this.cancel(!this.run);
      if (!this.isConnected || this.dataset.ready !== 'true') return;
      const run = { controller: new AbortController() };
      this.run = run;
      this.resetMore();
      this.classList.remove('is-empty');
      if (this.curatedNote) this.curatedNote.textContent = '';
      this.setAttribute('aria-busy', 'true');
      this.status.textContent = this.dataset.loadingLabel;
      if (document.activeElement === this.retry) {
        this.status.tabIndex = -1;
        this.status.focus({ preventScroll: true });
      }
      this.retry.hidden = true;
      this.list.replaceChildren();
      this.list.hidden = true;
      run.deadline = setTimeout(() => {
        if (!this.owns(run)) return;
        run.controller.abort();
        this.fail(run);
      }, 20000);
      try {
        const first = await this.page(1, run);
        const pages = [first];
        let next = 2;
        const worker = async () => {
          while (next <= first.pages && this.owns(run)) {
            const number = next++;
            pages[number - 1] = await this.page(number, run);
          }
        };
        await Promise.all([worker(), worker()]);
        if (!this.owns(run)) return;
        const seen = new Set();
        for (const page of pages) {
          if (!page || page.total !== first.total || page.pages !== first.pages) throw new Error('Catalogue changed during loading');
          for (const id of page.seen) {
            if (seen.has(id)) throw new Error('Repeated catalogue product');
            seen.add(id);
          }
        }
        if (seen.size !== first.total) throw new Error('Incomplete catalogue');
        const accepted = new Set([this.dataset.handle]);
        if (isHandle(this.dataset.alias)) accepted.add(this.dataset.alias);
        const matches = pages.flatMap(page => page.cards).filter(card => card.dataset.madeKeys.split('|').some(key => accepted.has(key)));
        matches.sort((a, b) => Number(b.dataset.madeAvailable === 'true') - Number(a.dataset.madeAvailable === 'true'));
        const curated = new Set([...this.closest('article').querySelectorAll('.emz-ingredient-products__card')].map(link => new URL(link.href, location.origin).pathname.split('/products/')[1]));
        const shown = matches.filter(card => !curated.has(card.dataset.madeHandle));
        const capped = this.more?.dataset.moreLabel && this.more.dataset.moreOneLabel && !this.expanded && shown.length > 10;
        const fragment = document.createDocumentFragment();
        shown.forEach((card, index) => {
          const imported = document.importNode(card, true);
          imported.hidden = Boolean(capped && index >= 8);
          fragment.append(imported);
        });
        this.list.replaceChildren(fragment);
        this.list.hidden = !shown.length;
        if (capped) {
          const remaining = shown.length - 8;
          this.more.textContent = (remaining === 1 ? this.more.dataset.moreOneLabel : this.more.dataset.moreLabel).replace('__COUNT__', String(remaining));
          this.more.hidden = false;
        } else if (this.more) this.more.setAttribute('aria-expanded', String(this.expanded === true));
        this.status.textContent = matches.length ? this.dataset.countLabel.replace('__COUNT__', String(matches.length)) : '';
        if (this.curatedNote) this.curatedNote.textContent = shown.length < matches.length ? this.dataset.curatedLabel : '';
        if (!matches.length) {
          if (document.activeElement === this.status) this.querySelector('.emz-made-with__fallback a')?.focus({ preventScroll: true });
          this.classList.add('is-empty');
        }
        this.removeAttribute('aria-busy');
        clearTimeout(run.deadline);
        document.dispatchEvent(new CustomEvent('emz:saved-controls:ready'));
        if (this.returnTask) { this.returnTask.run = run; this.restoreReturn(); }
      } catch (_) {
        if (this.run === run && this.isConnected) this.fail(run);
      } finally { clearTimeout(run.deadline); }
    }
    fail(run) {
      if (this.run !== run || !this.isConnected) return;
      run.controller.abort();
      this.cancelReturn();
      this.resetMore();
      this.classList.remove('is-empty');
      if (this.curatedNote) this.curatedNote.textContent = '';
      this.list.replaceChildren();
      this.list.hidden = true;
      this.removeAttribute('aria-busy');
      this.status.textContent = this.dataset.failedLabel;
      this.retry.hidden = false;
    }
  }
  customElements.define('emz-made-with', MadeWith);
})();
